import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  type ApplicationConfig,
  DOCUMENT,
  ErrorHandler,
  Injector,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideCheckNoChangesConfig
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import {
  Router,
  TitleStrategy,
  UrlSerializer,
  provideRouter,
  withComponentInputBinding,
  withViewTransitions
} from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { loadingBarInterceptor } from '@sds/loading-bar';
import { ConfigService, DeviceStore, ENVIRONMENT } from '@sds/shared';
import { provideMaterial } from '@sds/shared/material';
import { provideNavigationUrlSync } from '@sds/navigation-plants-list';
import { DateFnsDateAdapter } from '@sds/shared/utils';
import { TraceService, createErrorHandler } from '@sentry/angular';
import { OAuthService, OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';
import { provideHighcharts } from 'highcharts-angular';
import { provideLottieOptions } from 'ngx-lottie';
import { provideNgxSkeletonLoader } from 'ngx-skeleton-loader';
import { environment } from '../environments/environment';
import { environmentFactory, getE2eEnvironmentStorage } from './environment.factory';
import { apiServiceRootUrlsInitializer } from './initializers/api-service-root-urls.initializer';
import { appInitializer } from './initializers/app.initializer';
import { deviceMediaObserverInitializer } from './initializers/device-media-observer.initializer';
import { impersonationInitializer } from './initializers/impersonation.initializer';
import { tokensInitializer } from './initializers/tokens.initializer';
import { userObjectMercureInitializer } from './initializers/user-object-mercure.initializer';
import { WidgetFactoryInitializer, widgetFactory } from './initializers/widget-factory.initializer';
import { httpInterceptorProviders } from './interceptors';
import { storageFactory } from './oauth2.config';
import { TitleFactory } from './page-title.factory';
import { routes } from './routes';
import { providePlantSelectionConstraints } from './services/plant-selection-constraints.service';
import { translateLoaderFactory } from './translate-loader.factory';
import { CustomUrlSerializer } from './url-serializer';

export const appConfig: ApplicationConfig = {
  providers: [
    provideCheckNoChangesConfig({ exhaustive: false }),
    provideMaterial(),
    provideNavigationUrlSync(),
    providePlantSelectionConstraints(),
    provideHighcharts({
      // Modules compose once at evaluation time against whatever is already registered, and
      // `provideHighcharts` awaits them concurrently - so dependent pairs must be chained explicitly.
      // `export-data` composes against `Highcharts.Exporting`.
      modules: () => [
        import('highcharts/esm/modules/no-data-to-display'),
        import('highcharts/esm/modules/mouse-wheel-zoom'),
        import('highcharts/esm/modules/heatmap'),
        import('highcharts/esm/modules/boost'),
        import('highcharts/esm/modules/exporting').then(() => import('highcharts/esm/modules/export-data'))
      ]
    }),
    provideRouter(routes, withViewTransitions(), withComponentInputBinding()),
    provideOAuthClient(),
    provideLottieOptions({
      player: () => import('lottie-web')
    }),
    provideNgxSkeletonLoader(),
    provideHttpClient(withXhr(), withInterceptorsFromDi(), withInterceptors([loadingBarInterceptor])),
    provideTranslateService({
      loader: { provide: TranslateLoader, useFactory: translateLoaderFactory, deps: [HttpClient, ConfigService] }
    }),
    importProvidersFrom(
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.production,
        registrationStrategy: 'registerWhenStable:10000'
      })
    ),
    { provide: OAuthStorage, useFactory: storageFactory },
    provideAppInitializer(
      async (
        configService = inject(ConfigService),
        injector = inject(Injector),
        oauthService = inject(OAuthService)
      ) => {
        await configService.loadConfigurationData(environment.e2e);
        await apiServiceRootUrlsInitializer(configService, injector);
        await tokensInitializer(configService, injector);
        if (oauthService.hasValidAccessToken()) {
          await appInitializer(configService, injector);
          userObjectMercureInitializer(injector);
          await impersonationInitializer(injector);
        }
      }
    ),
    provideAppInitializer(() => deviceMediaObserverInitializer(inject(DeviceStore), inject(BreakpointObserver))()),
    provideAppInitializer(() => {
      widgetFactory(inject(WidgetFactoryInitializer))();
    }),
    provideAppInitializer(() => {
      inject(TraceService);
    }),
    httpInterceptorProviders,
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: false
      })
    },
    {
      provide: TraceService,
      deps: [Router]
    },
    {
      provide: ENVIRONMENT,
      useFactory: () => environmentFactory(environment, getE2eEnvironmentStorage(environment, inject(DOCUMENT)))
    },
    { provide: UrlSerializer, useClass: CustomUrlSerializer },
    { provide: TitleStrategy, useClass: TitleFactory },
    { provide: DateAdapter, useClass: DateFnsDateAdapter }
  ]
};
